// @flow
import {
  call,
  takeEvery,
  takeLatest,
  put,
  select,
  race,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { normalize } from 'normalizr';
import i18n from 'i18n-js';

import * as types from '../types/gateways';
import * as selectors from '../reducers';
import { REQUEST_TIMEOUT } from '../settings';
import { throwTimeout } from '../lib/http';
import * as gatewaysActions from '../actions/gateways';
import { School, Gateways } from '../api';
import HTTP_STATUS from '../lib/HTTPStatus';
import * as usersActions from '../actions/users';
import * as studentActions from '../actions/students';
import { arrayOfUsers } from '../api/schemas/users';
import { arrayOfGateways } from '../api/schemas/gateways';
import * as closeableActions from '../actions/closeable';
import * as temporalBannersActions from '../actions/temporalBanners';
import {
  DEFAULT_CREATE, DEFAULT_DURATION, DEFAULT_REMOVE, DEFAULT_UPDATE, DEFAULT_ADD, CREATE_ERROR, FETCH_ERROR, REMOVE_ERROR, DEFAULT_UNASIGN, UNASIGN_ERROR, ADD_ERROR, UPDATE_ERROR,
} from '../utils/errorDefaults';

function* createGateways(action) {
  const token = yield select(selectors.getToken);
  const profile = yield select(selectors.getProfileOfType, 'admin');
  const allowed_actions = [];
  if (action.payload.allow_exit) { allowed_actions.push('exit'); }
  if (action.payload.allow_entrance) { allowed_actions.push('entry'); }

  try {
    const { gatewayResponse, timeout } = yield race({
      gatewayResponse: call(
        [Gateways, 'create'],
        {
          token,
          data: {
            name: action.payload.name,
            ordinal: action.payload.ordinal,
            gate: parseInt(action.payload.gate, 10),
            allowed_actions,
            allow_change_requests: action.payload.allow_change_requests,
            school: profile.school,
            gateway_type: 1,
          },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('createGateway saga');
    }
    yield put(temporalBannersActions.shout(DEFAULT_CREATE(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(gatewaysActions.gatewayCreateSuccess(gatewayResponse));
    yield put(closeableActions.close('createexitmethod'));
  } catch (error) {
    yield put(temporalBannersActions.shout(CREATE_ERROR(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, -1, {}));
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(gatewaysActions.gatewayCreateFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al crear medio de salida' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* fetchGateways(action) {
  const token = yield select(selectors.getToken);
  const id = action.payload;

  try {
    // Fetch the next route
    const { gatewaysResponse, timeout } = yield race({
      gatewaysResponse: call(
        [School.custom, 'gateways'],
        { id, token },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchGateways saga');
    }
    const { entities, result } = normalize(
      gatewaysResponse,
      arrayOfGateways,
    );
    yield put(gatewaysActions.gatewaysFetchSucceed(
      entities.gateways,
      result,
    ));
  } catch (error) {
    yield put(temporalBannersActions.shout(FETCH_ERROR(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, -1, {}));
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(gatewaysActions.gatewayFetchFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* fetchGatewayUsers(action) {
  const token = yield select(selectors.getToken);
  const { id, filters } = action.payload;
  const { text = '', nextPage } = filters;
 
  const page = (nextPage == null || typeof nextPage === "undefined") ? 1 : nextPage;

  try {
    const { gatewaysUsersResponse, timeout } = yield race({
      gatewaysUsersResponse: call(
        [Gateways.custom, 'users'],
        { id, token, filters: { page, text, page_size: 15 } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchGatewayUsers saga');
    }


    const { entities, result } = normalize(
      gatewaysUsersResponse.results,
      arrayOfUsers,
    );

    for (let i = 0; i < result.length; i += 1) {
      const currentId = result[i];
      const user = entities.users[currentId];
      for (let j = 0; j < user.profiles.length; j += 1) {
        const currentProfile = user.profiles[j];
        if (currentProfile.type === 'student') {
          yield put(studentActions.fetchStudentStarted(currentProfile.id));
        }
      }
    }
    yield put(usersActions.fetchUsersSucceed(entities.users, result));

    const nextPage = gatewaysUsersResponse.next;

    yield put(gatewaysActions.updateGatewayUsersPage(id, nextPage));

    if (page === 1) {
      yield put(gatewaysActions.updateGatewayUsers(id, result));
    } else {
      yield put(gatewaysActions.gatewayUsersFetchSucceed({
        id,
        order: result,
        key: "users"
      }));
    }
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(FETCH_ERROR(i18n.t('labels.students', { count: 2 })), DEFAULT_DURATION, -1, {}));
    yield put(gatewaysActions.gatewayUsersFetchFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* removeGateway(action) {
  const id = action.payload;
  const token = yield select(selectors.getToken);
  try {
    const { timeout } = yield race({
      response: call(
        [Gateways, 'remove'],
        { token, id },
      ),
    });
    if (timeout) {
      throwTimeout('Remove gateway saga');
    }
    yield put(temporalBannersActions.shout(DEFAULT_REMOVE(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(gatewaysActions.removeGatewaySuccess(id));
  } catch (error) {
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(temporalBannersActions.shout(REMOVE_ERROR(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, -1, {}));
    yield put(gatewaysActions.removeGatewayFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* addGatewayUsers(action) {
  const token = yield select(selectors.getToken);
  const { id, user } = action.payload;
  const gateway = yield select(selectors.getGateway, id);
  try {
    // Fetch the next route
    const { gatewaysAddUsersResponse, timeout } = yield race({
      gatewaysAddUsersResponse: call(
        [Gateways.custom, 'addUser'],
        { id, token, data: { user } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchGatewayUsers saga');
    }
    const { entities, result } = normalize(
      [gatewaysAddUsersResponse],
      arrayOfUsers,
    );
    for (let i = 0; i < result.length; i += 1) {
      const currentId = result[i];
      const currentUser = entities.users[currentId];
      for (let j = 0; j < currentUser.profiles.length; j += 1) {
        const currentProfile = currentUser.profiles[j];
        if (currentProfile.type === 'student') {
          yield put(studentActions.fetchStudentStarted(currentProfile.id));
        }
      }
    }
    yield put(usersActions.fetchUsersSucceed(entities.users, result));
    const newUsers = gateway.users;
    if (newUsers.filter(u => u === result[0]).length === 0) {
      newUsers.push(result[0]);
    }
    yield put(temporalBannersActions.shout(DEFAULT_ADD(i18n.t('common.user', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(gatewaysActions.addGatewayUserSuccess({ id, users: newUsers }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(ADD_ERROR(i18n.t('labels.students', { count: 1 })), DEFAULT_DURATION, -1, {}));
    yield put(gatewaysActions.addGatewayUsersFetchFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* removeGatewayUsers(action) {
  const token = yield select(selectors.getToken);
  const { id, user } = action.payload;
  const student = yield select(selectors.getStudent, user);
  const gateway = yield select(selectors.getGateway, id);
  try {
    // Fetch the next route
    const { gatewaysAddUsersResponse, timeout } = yield race({
      gatewaysAddUsersResponse: call(
        [Gateways.custom, 'removeUser'],
        { id, token, data: { user: student.user.id } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchGatewayUsers saga');
    }
    const newUsers = gateway.users.filter(u => u !== student.user.id);
    yield put(temporalBannersActions.shout(DEFAULT_UNASIGN(i18n.t('labels.students', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(gatewaysActions.removeGatewayUserSuccess({ id, users: newUsers }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(UNASIGN_ERROR(i18n.t('labels.students', { count: 1 })), DEFAULT_DURATION, -1, {}));
    yield put(gatewaysActions.addGatewayUsersFetchFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}


function* updateGateway(action) {
  yield delay(2500);
  const token = yield select(selectors.getToken);
  try {
    // Update  route
    const { gatewaysResponse, timeout } = yield race({
      gatewaysResponse: call(
        [Gateways, 'update'],
        {
          token,
          id: action.payload.id,
          data: action.payload,
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('updateGateway saga');
    }
    yield put(temporalBannersActions.shout(DEFAULT_UPDATE(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(gatewaysActions.updateGatewaySucceed({
      ...gatewaysResponse,
      id: `${gatewaysResponse.id}`,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(UPDATE_ERROR(i18n.t('common.gateway', { count: 1 })), DEFAULT_DURATION, -1, {}));
    yield put(gatewaysActions.updateGatewayFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al crear ruta' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* updateGatewayActions(action) {
  yield delay(2500);
  const { id, intent } = action.payload;
  const token = yield select(selectors.getToken);
  try {
    // Update  route
    const { gatewaysResponse, timeout } = yield race({
      gatewaysResponse: call(
        [Gateways.custom, intent],
        {
          token,
          id,
          data: { action: action.payload.action },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('updateGateway saga');
    }

    yield put(gatewaysActions.updateGatewayActionSucceed({
      id,
      allowed_actions: gatewaysResponse,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(gatewaysActions.updateGatewayActionFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al crear ruta' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

export function* watchGatewaysFetch(): Iterator<any> {
  yield takeEvery(
    types.GATEWAYS_FETCH_STARTED,
    fetchGateways,
  );
}

export function* watchGatewaysCreate(): Iterator<any> {
  yield takeEvery(
    types.GATEWAY_CREATE_STARTED,
    createGateways,
  );
}

export function* watchRemoveGateway(): Iterator<any> {
  yield takeEvery(
    types.REMOVE_GATEWAY_STARTED,
    removeGateway,
  );
}

export function* watchGatewaysUsersFetch(): Iterator<any> {
  yield takeEvery(
    types.GATEWAY_USERS_FETCH_STARTED,
    fetchGatewayUsers,
  );
}

export function* watchAddGatewayUsers(): Iterator<any> {
  yield takeEvery(
    types.ADD_GATEWAY_USER_STARTED,
    addGatewayUsers,
  );
}

export function* watchRemoveGatewayUsers(): Iterator<any> {
  yield takeEvery(
    types.REMOVE_GATEWAY_USER_STARTED,
    removeGatewayUsers,
  );
}

export function* watchUpdateGateway(): Iterator<any> {
  yield takeLatest(
    types.UPDATE_GATEWAY_STARTED,
    updateGateway,
  );
}

export function* watchUpdateGatewayActions(): Iterator<any> {
  yield takeLatest(
    types.UPDATE_GATEWAY_ACTION_STARTED,
    updateGatewayActions,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/gateways.js